// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * ADR-080 twin — byte-identical in frontend/src/lib and mobile/src/lib (pinned by a
 * parity test next to the amyTurn twin). The client-side arithmetic for
 * "Milk (allergy)" vs "Lactose intolerance · level": the chip selection ↔ wire split,
 * the readers over a served hard-constraint set, and how a pending one-time
 * clarification is answered through the confirmed PUT. No policy lives here — the
 * server decides what blocks; a level is the user's choice and is never defaulted.
 */
import type {
  HardConstraint,
  HardConstraintClarification,
  HardConstraintsPutRequest,
  HardConstraintsResponse,
  LactoseLevel,
} from "@bitebalance/contracts"
import { LACTOSE_LEVELS, normalizeStructuredIntoleranceText } from "@bitebalance/contracts"

/** The pseudo-chip that sits in the allergen chip list next to the EU-14 allergens. */
export const LACTOSE_CHIP = "intolerance:lactose"

export type ListOrNone = string[] | "none" | undefined

/** A step-6 draft entry; the level is absent while the user has not chosen one (the wire schema then rejects the answer → Continue stays disabled). */
export interface StructuredIntoleranceDraft {
  subjectId: "lactose"
  level?: LactoseLevel
}

export function isLactoseLevel(value: unknown): value is LactoseLevel {
  return typeof value === "string" && (LACTOSE_LEVELS as readonly string[]).includes(value)
}

/** Wire allergens + whether the lactose chip is on, from one chip selection. */
export function splitChipSelection(selection: ListOrNone): {
  allergens: ListOrNone
  lactoseSelected: boolean
} {
  if (selection === undefined || selection === "none")
    return { allergens: selection, lactoseSelected: false }
  const lactoseSelected = selection.includes(LACTOSE_CHIP)
  const allergens = selection.filter(v => v !== LACTOSE_CHIP)
  // Only the lactose chip is on: the allergen group IS answered — explicitly none.
  return {
    allergens: allergens.length === 0 && lactoseSelected ? "none" : allergens,
    lactoseSelected,
  }
}

/** The chip selection to render for wire allergens + the lactose flag. */
export function mergeChipSelection(allergens: ListOrNone, lactoseSelected: boolean): ListOrNone {
  if (!lactoseSelected) return allergens
  return [...(Array.isArray(allergens) ? allergens : []), LACTOSE_CHIP]
}

export function structuredIntolerancesOf(draft: unknown): StructuredIntoleranceDraft[] {
  if (!Array.isArray(draft)) return []
  return draft.filter(
    (entry): entry is StructuredIntoleranceDraft =>
      typeof entry === "object" &&
      entry !== null &&
      (entry as { subjectId?: unknown }).subjectId === "lactose"
  )
}

export function draftLactose(list: StructuredIntoleranceDraft[]): {
  selected: boolean
  level: LactoseLevel | null
} {
  const entry = list.find(e => e.subjectId === "lactose")
  return {
    selected: entry !== undefined,
    level: entry && isLactoseLevel(entry.level) ? entry.level : null,
  }
}

export function withLactoseDraft(
  list: StructuredIntoleranceDraft[],
  selected: boolean,
  level: LactoseLevel | null
): StructuredIntoleranceDraft[] {
  const others = list.filter(e => e.subjectId !== "lactose")
  if (!selected) return others
  return [...others, { subjectId: "lactose", ...(level ? { level } : {}) }]
}

/** True when free text NAMES the structured lactose subject (the same phrasing list the API and migration 0045 use). */
export function isStructuredIntoleranceText(value: string): boolean {
  return normalizeStructuredIntoleranceText(value) !== null
}

/**
 * A free-text intolerance change with the structured phrasings removed: "Laktose" typed
 * into the free-text box is redirected to the "Lactose intolerance" choice (the level is
 * the user's, never guessed). `rejected` tells the caller to show that hint; when nothing
 * else was added the previous value is kept (an unanswered field stays unanswered).
 */
export function guardIntoleranceText(
  previous: ListOrNone,
  next: ListOrNone
): { value: ListOrNone; rejected: boolean } {
  if (!Array.isArray(next)) return { value: next, rejected: false }
  const kept = next.filter(entry => !isStructuredIntoleranceText(entry))
  if (kept.length === next.length) return { value: next, rejected: false }
  if (kept.length > 0) return { value: kept, rejected: true }
  return {
    value:
      Array.isArray(previous) && previous.length > 0
        ? previous
        : previous === "none"
          ? "none"
          : undefined,
    rejected: true,
  }
}

/** The active lactose level to flag a recipe with (a legacy row without a level reads as the strict one). */
export function lactoseFlagLevel(constraints: HardConstraint[]): LactoseLevel | null {
  const lactose = activeLactose(constraints)
  if (!lactose) return null
  return lactose.level ?? "avoid_all_dairy"
}

/** `moreProfile.constraints.<key>` — the short level label. */
export function lactoseLevelKey(level: LactoseLevel): "levelAvoidAll" | "levelFreeOk" {
  return level === "avoid_all_dairy" ? "levelAvoidAll" : "levelFreeOk"
}

const isActive = (c: HardConstraint) => c.status === "active"

/** The active structured lactose intolerance of a served set (`level` null on a legacy row). */
export function activeLactose(
  constraints: HardConstraint[]
): { level: LactoseLevel | null } | null {
  const row = constraints.find(
    c => isActive(c) && c.type === "intolerance" && c.subjectId === "lactose"
  )
  return row ? { level: isLactoseLevel(row.level) ? row.level : null } : null
}

/** Free-text intolerances (as the user typed them), never the structured subject. */
export function freeTextIntolerances(constraints: HardConstraint[]): string[] {
  return constraints
    .filter(c => isActive(c) && c.type === "intolerance" && c.subjectId !== "lactose")
    .map(c => c.subjectId)
}

export type ClarificationAnswer =
  | { kind: "milk_or_lactose"; choice: "allergy" }
  | { kind: "milk_or_lactose"; choice: "intolerance"; level: LactoseLevel }
  | { kind: "lactose_level"; level: LactoseLevel }

/** The confirmed PUT that answers ONE pending clarification, built from the served set. */
export function answerClarification(
  current: HardConstraintsResponse,
  answer: ClarificationAnswer
): HardConstraintsPutRequest {
  const active = current.constraints.filter(isActive)
  const withoutMilkAllergy = active.filter(c => !(c.type === "allergen" && c.subjectId === "milk"))
  const withoutLactose = (list: HardConstraint[]) =>
    list.filter(c => !(c.type === "intolerance" && c.subjectId === "lactose"))
  const lactoseRow = (level: LactoseLevel): HardConstraint => ({
    type: "intolerance",
    subjectId: "lactose",
    status: "active",
    level,
  })
  let constraints: HardConstraint[]
  if (answer.kind === "milk_or_lactose" && answer.choice === "allergy") {
    constraints = withoutLactose(active)
  } else if (answer.kind === "milk_or_lactose") {
    constraints = [...withoutLactose(withoutMilkAllergy), lactoseRow(answer.level)]
  } else {
    constraints = [...withoutLactose(active), lactoseRow(answer.level)]
  }
  // An answer that carries a level settles a co-pending "which level?" question too —
  // the user is never asked the level twice.
  const pendingKinds = new Set((current.clarifications ?? []).map(c => c.kind))
  const resolveClarifications: HardConstraintClarification[] = [answer.kind]
  if ("level" in answer && answer.kind !== "lactose_level" && pendingKinds.has("lactose_level"))
    resolveClarifications.push("lactose_level")
  return { baseRevision: current.revision, confirmed: true, constraints, resolveClarifications }
}

/** Which Today line to show for the pending kinds (the milk question first; none → null). */
export function pendingLineKey(
  kinds: HardConstraintClarification[]
): "clarifyLine" | "clarifyLevelLine" | null {
  if (kinds.includes("milk_or_lactose")) return "clarifyLine"
  if (kinds.includes("lactose_level")) return "clarifyLevelLine"
  return null
}
